/**
 * Smooth scroll-driven playback for spinning bottle videos.
 * Plays while scrolling and pauses when scrolling settles.
 */

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

/**
 * @param {string | undefined} value
 * @param {number} fallback
 */
const parseNumericDatasetValue = (value, fallback) => {
  const parsed = Number.parseFloat(value || '')
  return Number.isFinite(parsed) ? parsed : fallback
}

const isIOS = () => {
  const userAgent = navigator.userAgent || ''
  return /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

const supportsVideoFrameCallback =
  typeof HTMLVideoElement !== 'undefined' &&
  'requestVideoFrameCallback' in HTMLVideoElement.prototype

const spinningVideoPlayers = new Map()

/** @param {HTMLElement} container */
const createSpinningVideoPlayer = (container) => {
  const video = container.querySelector('video')
  if (!(video instanceof HTMLVideoElement)) return null

  const maybeCanvas = container.querySelector('canvas')
  const shouldUseCanvas = isIOS() && maybeCanvas instanceof HTMLCanvasElement

  const idleDelay = parseNumericDatasetValue(container.dataset.idleDelay, 120)
  const speedMultiplier = parseNumericDatasetValue(container.dataset.speedMultiplier, 1)
  const minRate = parseNumericDatasetValue(container.dataset.minRate, 0.7)
  const maxRate = parseNumericDatasetValue(container.dataset.maxRate, 1.9)
  const startProgress = clamp(parseNumericDatasetValue(container.dataset.startProgress, 0), 0, 1)

  /** @type {CanvasRenderingContext2D | null} */
  let ctx = null
  let pauseTimer = 0
  let pendingScroll = false
  let isVisible = false
  let isPlaying = false
  let destroyed = false
  let lastScrollY = window.scrollY
  let lastScrollTs = performance.now()
  let rafId = 0
  let frameCallbackId = 0
  let reverseRafId = 0
  let reverseLastTs = 0
  let reverseRateTarget = 0
  let reverseRateCurrent = 0
  let reverseVirtualTime = 0
  let reverseLastSeekTs = 0
  let hasAppliedStartFrame = false
  let lastDrawnTime = -1

  const REVERSE_RATE_LERP = 0.24
  const REVERSE_SEEK_INTERVAL_MS = 16

  const clearPauseTimer = () => {
    if (!pauseTimer) return
    window.clearTimeout(pauseTimer)
    pauseTimer = 0
  }

  const cancelFrameLoop = () => {
    if (supportsVideoFrameCallback && frameCallbackId) {
      video.cancelVideoFrameCallback(frameCallbackId)
      frameCallbackId = 0
    }
    if (rafId) {
      window.cancelAnimationFrame(rafId)
      rafId = 0
    }
  }

  /** @param {number} timeValue */
  const normalizeLoopTime = (timeValue) => {
    const duration = video.duration
    if (!duration || duration <= 0) return 0
    return ((timeValue % duration) + duration) % duration
  }

  const stopReverseLoop = () => {
    if (reverseRafId) {
      window.cancelAnimationFrame(reverseRafId)
      reverseRafId = 0
    }
    reverseLastTs = 0
    reverseRateTarget = 0
    reverseRateCurrent = 0
    reverseLastSeekTs = 0
  }

  const applyStartFrame = () => {
    if (hasAppliedStartFrame) return
    if (!video.duration || video.readyState < video.HAVE_METADATA) return

    const duration = video.duration
    const safeMaxTime = Math.max(duration - 0.001, 0)
    const startTime = clamp(duration * startProgress, 0, safeMaxTime)

    if (Math.abs(video.currentTime - startTime) > 0.001) {
      video.currentTime = startTime
    }

    hasAppliedStartFrame = true
  }

  const updateCanvasSize = () => {
    if (!shouldUseCanvas || !(maybeCanvas instanceof HTMLCanvasElement)) return
    if (video.videoWidth <= 0 || video.videoHeight <= 0) return

    const rect = container.getBoundingClientRect()
    const cssWidth = Math.max(Math.round(rect.width), 1)
    const cssHeight = Math.max(Math.round((cssWidth * video.videoHeight) / video.videoWidth), 1)
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    maybeCanvas.style.width = `${cssWidth}px`
    maybeCanvas.style.height = `${cssHeight}px`
    maybeCanvas.width = Math.max(Math.round(cssWidth * dpr), 1)
    maybeCanvas.height = Math.max(Math.round(cssHeight * dpr), 1)

    ctx = maybeCanvas.getContext('2d', { alpha: true })
    container.setAttribute('data-smooth-canvas', 'on')

    // Resizing the canvas clears it, so force the next drawFrame to repaint
    // even if the video time has not changed.
    lastDrawnTime = -1
  }

  const drawFrame = () => {
    if (!shouldUseCanvas || !(maybeCanvas instanceof HTMLCanvasElement) || !ctx) return
    if (video.readyState < video.HAVE_CURRENT_DATA) return
    if (video.currentTime === lastDrawnTime) return
    lastDrawnTime = video.currentTime

    const width = maybeCanvas.width
    const height = maybeCanvas.height

    ctx.clearRect(0, 0, width, height)
    ctx.drawImage(video, 0, 0, width, height)

    // The iOS fallback plays the MP4, which has a solid white background
    // rather than an alpha channel. Key near-white pixels out to transparent
    // so the bottle composites cleanly over the page — mix-blend-mode is
    // unreliable on iOS, so we produce real alpha here instead.
    //
    // getImageData throws if the canvas is tainted by a cross-origin video;
    // the <video crossorigin="anonymous"> attribute plus Shopify's CDN CORS
    // headers prevent that, but guard so a failure never kills the loop.
    let imageData
    try {
      imageData = ctx.getImageData(0, 0, width, height)
    } catch (error) {
      return
    }

    const pixels = new Uint32Array(imageData.data.buffer)

    for (let i = 0; i < pixels.length; i++) {
      const px = pixels[i]
      if(!px) continue
      // Little-endian byte order is 0xAABBGGRR.
      const r = px & 0xff
      const g = (px >> 8) & 0xff
      const b = (px >> 16) & 0xff
      const brightness = (r + g + b) / 3

      if (brightness > 240) {
        pixels[i] = 0 // near-white → fully transparent
      } else if (brightness > 200) {
        // Feather zone: fade alpha for smooth, halo-free edges.
        const alpha = 255 - Math.round(((brightness - 200) * 255) / 40)
        pixels[i] = (alpha << 24) | (px & 0x00ffffff)
      }
    }

    ctx.putImageData(imageData, 0, 0)
  }

  /** @param {number} timestamp */
  const tickReversePlayback = (timestamp) => {
    if (destroyed || !isVisible) {
      stopReverseLoop()
      return
    }

    if (!video.duration || video.readyState < video.HAVE_CURRENT_DATA) {
      reverseRafId = window.requestAnimationFrame(tickReversePlayback)
      return
    }

    if (!reverseLastTs) {
      reverseLastTs = timestamp
      reverseRafId = window.requestAnimationFrame(tickReversePlayback)
      return
    }

    const deltaSeconds = Math.max((timestamp - reverseLastTs) / 1000, 0)
    reverseLastTs = timestamp

    if (deltaSeconds > 0) {
      // Ease reverse velocity changes to avoid visible stutter on quick scroll deltas.
      reverseRateCurrent += (reverseRateTarget - reverseRateCurrent) * REVERSE_RATE_LERP
      reverseVirtualTime = normalizeLoopTime(reverseVirtualTime - (reverseRateCurrent * deltaSeconds))

      // Seek at most once per frame and only when the decoder is ready.
      if (!video.seeking && timestamp - reverseLastSeekTs >= REVERSE_SEEK_INTERVAL_MS) {
        video.currentTime = reverseVirtualTime
        reverseLastSeekTs = timestamp
      }

      drawFrame()
    }

    reverseRafId = window.requestAnimationFrame(tickReversePlayback)
  }

  const queueFrameDraw = () => {
    if (!shouldUseCanvas || destroyed || !isPlaying) return

    if (supportsVideoFrameCallback) {
      frameCallbackId = video.requestVideoFrameCallback(() => {
        drawFrame()
        queueFrameDraw()
      })
      return
    }

    rafId = window.requestAnimationFrame(() => {
      drawFrame()
      queueFrameDraw()
    })
  }

  const pausePlayback = () => {
    clearPauseTimer()
    isPlaying = false
    stopReverseLoop()
    cancelFrameLoop()
    video.pause()
    drawFrame()
  }

  /** @param {number} rate */
  const playPlayback = (rate) => {
    if (!isVisible || destroyed) return

    stopReverseLoop()
    video.playbackRate = clamp(rate, minRate, maxRate)

    if (!video.paused) {
      if (!isPlaying) {
        isPlaying = true
      }
      if (shouldUseCanvas && !frameCallbackId && !rafId) {
        queueFrameDraw()
      }
      return
    }

    const playPromise = video.play()
    if (playPromise === undefined) {
      isPlaying = true
      if (shouldUseCanvas) {
        updateCanvasSize()
        queueFrameDraw()
      }
      return
    }

    playPromise
      .then(() => {
        if (destroyed) return
        isPlaying = true
        if (shouldUseCanvas) {
          updateCanvasSize()
          queueFrameDraw()
        }
      })
      .catch(() => {
        // Ignore autoplay rejection; user scroll interaction will retry.
      })
  }

  /** @param {number} rate */
  const playReverse = (rate) => {
    if (!isVisible || destroyed) return

    reverseRateTarget = clamp(rate, minRate, maxRate)
    isPlaying = false

    cancelFrameLoop()
    video.pause()

    if (!reverseRafId) {
      reverseVirtualTime = video.currentTime || 0
      reverseRateCurrent = reverseRateTarget
      reverseLastSeekTs = performance.now()
      reverseRafId = window.requestAnimationFrame(tickReversePlayback)
    }
  }

  const schedulePause = () => {
    clearPauseTimer()
    pauseTimer = window.setTimeout(() => {
      pausePlayback()
    }, idleDelay)
  }

  const processScroll = () => {
    pendingScroll = false

    const now = performance.now()
    const scrollY = window.scrollY
    const deltaY = scrollY - lastScrollY
    const deltaTime = Math.max(now - lastScrollTs, 16)

    lastScrollY = scrollY
    lastScrollTs = now

    if (!isVisible || destroyed) return

    applyStartFrame()

    if (Math.abs(deltaY) < 0.5) return

    const velocity = Math.abs(deltaY) / deltaTime
    const rate = clamp(velocity * speedMultiplier, minRate, maxRate)

    if (deltaY > 0) {
      playPlayback(rate)
    } else {
      playReverse(rate)
    }

    schedulePause()
  }

  const onScroll = () => {
    if (pendingScroll || destroyed) return
    pendingScroll = true
    window.requestAnimationFrame(processScroll)
  }

  const onResize = () => {
    if (destroyed) return
    updateCanvasSize()
    drawFrame()
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isVisible = entry.isIntersecting

        if (!isVisible) {
          pausePlayback()
          return
        }

        // Sync scroll sampling when the media enters viewport to avoid large first deltas.
        lastScrollY = window.scrollY
        lastScrollTs = performance.now()

        applyStartFrame()
        updateCanvasSize()
        drawFrame()
      })
    },
    {
      threshold: 0.01,
      rootMargin: '0px'
    }
  )

  const onLoadedMetadata = () => {
    applyStartFrame()
    onResize()
  }

  video.addEventListener('loadedmetadata', onLoadedMetadata)
  video.addEventListener('loadeddata', drawFrame)
  window.addEventListener('resize', onResize, { passive: true })
  window.addEventListener('scroll', onScroll, { passive: true })

  observer.observe(container)
  applyStartFrame()
  updateCanvasSize()
  drawFrame()

  return {
    destroy() {
      destroyed = true
      pausePlayback()
      observer.disconnect()
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('loadeddata', drawFrame)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll)
    }
  }
}

/** @param {Document | Element} [root=document] */
const initVideoSpinningSmooth = (root = document) => {
  const containers = root.querySelectorAll('.js-video-spinning-smooth-container')

  containers.forEach((container) => {
    if (!(container instanceof HTMLElement)) return
    if (spinningVideoPlayers.has(container)) return
    const player = createSpinningVideoPlayer(container)
    if (!player) return
    spinningVideoPlayers.set(container, player)
  })
}

/** @param {Document | Element} [root=document] */
const destroyVideoSpinningSmooth = (root = document) => {
  const containers = root.querySelectorAll('.js-video-spinning-smooth-container')

  containers.forEach((container) => {
    if (!(container instanceof HTMLElement)) return
    const player = spinningVideoPlayers.get(container)
    if (!player) return
    player.destroy()
    spinningVideoPlayers.delete(container)
  })
}

document.addEventListener('DOMContentLoaded', () => {
  initVideoSpinningSmooth()
})

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return
  initVideoSpinningSmooth(event.target)
})

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return
  destroyVideoSpinningSmooth(event.target)
})

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  initVideoSpinningSmooth()
}
